import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ChangePasswordModal from "../../components/modal/ChangePasswordModal";
import LogoutFromAllDevices from "../../components/modal/LogoutFromAllDevicesModal";
import AddMemberModal from "../../components/modal/AddMemberModal";
import EditMemberModal from "../../components/modal/EditMemberModal";
import DeleteConfirmationModal from "../../components/modal/DeleteConfirmationModal";
import MemberTable from "../../components/MemberTable";
import { useDashboard } from "../dashboard/hooks/useDashboard";
import { useDeviceStore } from "../../store/useDeviceStore";
import {
  useGetUserDevices,
  useGetAllUsers,
  useSignUpUser,
  useDeleteUser,
  useUpdateUserRole,
  useUpdateProfile,
} from "../../queries/admin";
import { useAuthStore } from "../../store/useAuthStore";
import { PlusIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { classNames } from "../../utils/string";
import { useIsAdmin } from "../../utils/auth";
import { toast } from "react-toastify";
import { Member, MemberCreateDto, MemberUpdateDto } from "../../types/device";
import { Icon } from "@iconify/react";
import { WidgetTemplate } from "../dashboard/types/dashboard";
import LocationSelection from "../new/LocationSelection";
import DeviceSelection from "../new/DeviceSelection";
import { FloorPlanUpload } from "../../components/widgets/map/FloorPlanUpload";
import ReportWidget from "../../widgets/report/ReportWidget";
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const TABS = [
  { key: "Profile" as const, label: "Profile", icon: "mdi:account-outline" },
  { key: "Access" as const, label: "Access", icon: "mdi:shield-account-outline" },
  { key: "Widgets" as const, label: "Widgets", icon: "mdi:widgets-outline" },
  { key: "Billing" as const, label: "Billing", icon: "mdi:credit-card-outline" },
];
type Tab = (typeof TABS)[number]["key"];

// ── Profile Tab ──────────────────────────────────────────────────────────────

// Indian GSTIN: 2-digit state code + 10-char PAN + entity code + "Z" + checksum
const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
// 10-digit Indian mobile number, optional +91 prefix
const PHONE_REGEX = /^(\+91[\s-]?)?[6-9]\d{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MAX_PROFILE_IMAGE_SIZE = 2 * 1024 * 1024; // 2MB

type ProfileFieldErrors = Partial<
  Record<"orgName" | "displayName" | "phone" | "email" | "address" | "gstNo", string>
>;

function validateProfile(values: {
  orgName: string;
  displayName: string;
  phone: string;
  email: string;
  address: string;
  gstNo: string;
}): ProfileFieldErrors {
  const errors: ProfileFieldErrors = {};

  const orgName = values.orgName.trim();
  if (!orgName) {
    errors.orgName = "Organisation name is required";
  } else if (orgName.length < 2 || orgName.length > 100) {
    errors.orgName = "Organisation name must be between 2 and 100 characters";
  }

  const displayName = values.displayName.trim();
  if (!displayName) {
    errors.displayName = "User name is required";
  } else if (displayName.length < 2 || displayName.length > 100) {
    errors.displayName = "User name must be between 2 and 100 characters";
  } else if (!/^[a-zA-Z][a-zA-Z0-9 .'-]*$/.test(displayName)) {
    errors.displayName =
      "User name must start with a letter and contain only letters, numbers, spaces, and . ' -";
  }

  const phone = values.phone.trim();
  if (phone && !PHONE_REGEX.test(phone)) {
    errors.phone = "Enter a valid 10-digit mobile number (optionally with +91)";
  }

  const email = values.email.trim();
  if (!email) {
    errors.email = "E-mail is required";
  } else if (!EMAIL_REGEX.test(email)) {
    errors.email = "Enter a valid e-mail address";
  }

  const address = values.address.trim();
  if (address && address.length > 250) {
    errors.address = "Address must be 250 characters or fewer";
  }

  const gstNo = values.gstNo.trim().toUpperCase();
  if (gstNo && !GST_REGEX.test(gstNo)) {
    errors.gstNo = "Enter a valid 15-character GST number (e.g. 22AAAAA0000A1Z5)";
  }

  return errors;
}

const inputClass = (hasError: boolean) =>
  classNames(
    "block w-full rounded-lg border bg-white dark:bg-gray-800 px-3.5 py-2.5 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-1 outline-none transition-colors duration-200",
    hasError
      ? "border-red-500 dark:border-red-500 focus:border-red-500 focus:ring-red-500"
      : "border-gray-300 dark:border-gray-600 focus:border-primary focus:ring-primary",
  );

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">{message}</p>;
}

function ProfileTab() {
  const { data: user } = useGetUserDevices();
  const { name: userName, email: userEmail } = useAuthStore();
  const [dialog, setDialog] = useState(false);
  const [logOutDialog, setLogoutDialog] = useState(false);

  const [orgName, setOrgName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [gstNo, setGstNo] = useState("");
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const updateProfileMutation = useUpdateProfile();

  useEffect(() => {
    if (user) {
      setOrgName(user.client?.name ?? "");
      setDisplayName(user.name ?? userName ?? "");
      setEmail(user.email ?? userEmail ?? "");
      setPhone(user.phone ?? "");
      setAddress(user.address ?? "");
      setGstNo(user.gstNo ?? "");
      setProfileImage(user.profileImage ?? null);
    }
  }, [user, userName, userEmail]);

  return (
    <>
      {dialog && <ChangePasswordModal isOpen={dialog} onClose={() => setDialog(false)} />}
      {logOutDialog && (
        <LogoutFromAllDevices isOpen={logOutDialog} onClose={() => setLogoutDialog(false)} />
      )}

      <div className="space-y-8">
        {/* Avatar & Name */}
        <div className="flex items-center gap-5">
          <div className="relative group">
            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md overflow-hidden">
              {profileImage ? (
                <img src={profileImage} alt="Profile" className="h-full w-full object-cover" />
              ) : (
                <span className="text-2xl font-bold text-white">
                  {(displayName || email || "U").charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <label
              htmlFor="profile-image-upload"
              className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer"
            >
              <Icon icon="mdi:camera" className="w-5 h-5 text-white" />
            </label>
            <input
              id="profile-image-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (ev) => {
                    setProfileImage(ev.target?.result as string);
                  };
                  reader.readAsDataURL(file);
                }
              }}
            />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {displayName || "User"}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{email}</p>
          </div>
        </div>

        <hr className="border-gray-200 dark:border-gray-700" />

        {/* Form Fields */}
        <div className="grid grid-cols-1 gap-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Organisation Name
            </label>
            <input
              type="text"
              value={orgName}
              readOnly
              disabled
              placeholder="Organisation name"
              className="block w-full cursor-not-allowed rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700/50 px-3.5 py-2.5 text-sm text-gray-500 dark:text-gray-400 placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              User Name
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter user name"
              className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3.5 py-2.5 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors duration-200"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Phone Number
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter phone number"
                className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3.5 py-2.5 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors duration-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                E-Mail
              </label>
              <input
                type="email"
                value={email}
                readOnly
                disabled
                placeholder="E-mail"
                className="block w-full cursor-not-allowed rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700/50 px-3.5 py-2.5 text-sm text-gray-500 dark:text-gray-400 placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Address
            </label>
            <textarea
              rows={3}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Enter address"
              className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3.5 py-2.5 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors duration-200 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              GST No.
            </label>
            <input
              type="text"
              value={gstNo}
              onChange={(e) => setGstNo(e.target.value)}
              placeholder="Enter GST number"
              className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3.5 py-2.5 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors duration-200"
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              if (user) {
                setOrgName(user.client?.name ?? "");
                setDisplayName(user.name ?? userName ?? "");
                setEmail(user.email ?? userEmail ?? "");
                setPhone(user.phone ?? "");
                setAddress(user.address ?? "");
                setGstNo(user.gstNo ?? "");
                setProfileImage(user.profileImage ?? null);
              }
            }}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={updateProfileMutation.isPending}
            onClick={() => {
              updateProfileMutation.mutate({
                name: displayName,
                phone,
                address,
                gstNo,
                profileImage: profileImage || undefined,
              });
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updateProfileMutation.isPending ? "Saving..." : "Save Changes"}
          </button>
        </div>

        <hr className="border-gray-200 dark:border-gray-700" />

        {/* Security Section */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Security</h4>
          <div className="space-y-3">
            <button
              onClick={() => setDialog(true)}
              className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 group"
            >
              <div className="p-1.5 bg-blue-100 dark:bg-blue-900/40 rounded-md group-hover:bg-blue-200 dark:group-hover:bg-blue-900/60 transition-colors">
                <Icon
                  icon="mdi:lock-outline"
                  className="w-4 h-4 text-blue-600 dark:text-blue-400"
                />
              </div>
              <span className="font-medium">Change Password</span>
              <Icon icon="mdi:chevron-right" className="w-4 h-4 ml-auto text-gray-400" />
            </button>
            <button
              onClick={() => setLogoutDialog(true)}
              className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-colors duration-200 group"
            >
              <div className="p-1.5 bg-red-100 dark:bg-red-900/40 rounded-md group-hover:bg-red-200 dark:group-hover:bg-red-900/60 transition-colors">
                <Icon icon="mdi:logout" className="w-4 h-4 text-red-600 dark:text-red-400" />
              </div>
              <span className="font-medium">Log out from all devices</span>
              <Icon icon="mdi:chevron-right" className="w-4 h-4 ml-auto text-gray-400" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Access Tab ────────────────────────────────────────────────────────────────

function AccessTab() {
  const isAdmin = useIsAdmin();
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showEditMemberModal, setShowEditMemberModal] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalConfig, setDeleteModalConfig] = useState<{
    title: string;
    itemName: string;
    itemType: string;
    onConfirm: () => void;
  } | null>(null);

  const { data: usersData, isLoading, error } = useGetAllUsers();
  const signUpMutation = useSignUpUser();
  const deleteMutation = useDeleteUser();
  const updateRoleMutation = useUpdateUserRole();

  // Map backend users to frontend Member type
  const members: Member[] = (usersData || []).map((user: any) => ({
    id: user.id,
    email: user.email,
    name: user.name,
    access: user.role === "ADMIN" ? "Admin" : "User",
    createdAt: user.createdAt,
    updatedAt: user.updatedAt,
  }));

  const adminCount = members.filter((m) => m.access === "Admin").length;
  const isLastAdmin = (member: Member) => member.access === "Admin" && adminCount <= 1;

  const handleAddMember = (memberData: MemberCreateDto) => {
    signUpMutation.mutate(
      {
        email: memberData.email,
        name: memberData.name,
        role: memberData.access === "Admin" ? "ADMIN" : "USER",
        ...(memberData.password ? { password: memberData.password } : {}),
      },
      {
        onSuccess: () => {
          setShowAddMemberModal(false);
        },
      },
    );
  };

  const handleEditMember = (member: Member) => {
    setEditingMember(member);
    setShowEditMemberModal(true);
  };

  const handleUpdateMember = (memberId: string, memberData: MemberUpdateDto) => {
    if (memberData.access) {
      const role = memberData.access === "Admin" ? "ADMIN" : "USER";
      updateRoleMutation.mutate(
        { id: memberId, role },
        {
          onSuccess: () => {
            setShowEditMemberModal(false);
            setEditingMember(null);
          },
        },
      );
    }
  };

  const handleDeleteMember = (memberId: string, memberEmail: string) => {
    const member = members.find((m) => m.id === memberId);
    if (member && isLastAdmin(member)) {
      toast.error("Cannot remove the only admin. Assign another admin first.");
      return;
    }
    setDeleteModalConfig({
      title: "Remove Member",
      itemName: memberEmail,
      itemType: "member",
      onConfirm: () => {
        deleteMutation.mutate(memberId, {
          onSuccess: () => {
            setShowDeleteModal(false);
            setDeleteModalConfig(null);
          },
        });
      },
    });
    setShowDeleteModal(true);
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48" />
        <div className="grid grid-cols-3 gap-4">
          <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg" />
          <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg" />
          <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        </div>
        <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-sm text-red-700 dark:text-red-300">
          Failed to load members: {error.message}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Team Members</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage who has access to your organisation.
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={() => setShowAddMemberModal(true)}
              className="inline-flex items-center gap-x-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary/90 transition-colors duration-200"
            >
              <PlusIcon className="h-4 w-4" />
              Add Member
            </button>
          )}
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Icon
                icon="mdi:account-group-outline"
                className="w-4 h-4 text-blue-600 dark:text-blue-400"
              />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Total Members
              </span>
            </div>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              {members.length}
            </span>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Icon
                icon="mdi:shield-crown-outline"
                className="w-4 h-4 text-purple-600 dark:text-purple-400"
              />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Admins</span>
            </div>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              {members.filter((m) => m.access === "Admin").length}
            </span>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Icon
                icon="mdi:account-outline"
                className="w-4 h-4 text-green-600 dark:text-green-400"
              />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Users</span>
            </div>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              {members.filter((m) => m.access === "User").length}
            </span>
          </div>
        </div>

        {/* Table */}
        <MemberTable
          members={members}
          onEdit={handleEditMember}
          onDelete={handleDeleteMember}
          canManage={isAdmin}
        />
      </div>

      {showAddMemberModal && (
        <AddMemberModal
          isOpen={showAddMemberModal}
          onClose={() => setShowAddMemberModal(false)}
          onSubmit={handleAddMember}
          isLoading={signUpMutation.isPending}
        />
      )}
      {showEditMemberModal && editingMember && (
        <EditMemberModal
          isOpen={showEditMemberModal}
          onClose={() => {
            setShowEditMemberModal(false);
            setEditingMember(null);
          }}
          member={editingMember}
          onSubmit={handleUpdateMember}
          isLastAdmin={isLastAdmin(editingMember)}
        />
      )}
      {showDeleteModal && deleteModalConfig && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setDeleteModalConfig(null);
          }}
          onConfirm={deleteModalConfig.onConfirm}
          title={deleteModalConfig.title}
          itemName={deleteModalConfig.itemName}
          itemType={deleteModalConfig.itemType}
        />
      )}
    </>
  );
}

// ── Widgets Tab ──────────────────────────────────────────────────────────────

function SortableWidgetCard({
  template,
  isAdded,
  isTableUI,
  isPending,
  isExpanded,
  onToggleExpand,
  onToggleWidget,
  children,
}: {
  template: WidgetTemplate;
  isAdded: boolean;
  isTableUI: boolean;
  isPending: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onToggleWidget: (checked: boolean) => void;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: template.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classNames(
        "rounded-lg border overflow-hidden transition-all duration-200",
        isAdded
          ? "border-primary/50 dark:border-primary/40"
          : "border-gray-200 dark:border-gray-700 opacity-60",
        isPending ? "ring-2 ring-amber-400/50" : "",
        isDragging ? "shadow-lg" : ""
      )}
    >
      {/* Selection header bar */}
      <div
        className={classNames(
          "flex items-center justify-between px-4 py-2.5",
          isAdded
            ? "bg-primary/5 dark:bg-primary/10"
            : "bg-gray-50 dark:bg-gray-800"
        )}
      >
        {/* Drag handle */}
        <button
          {...attributes}
          {...listeners}
          className="flex-shrink-0 cursor-grab active:cursor-grabbing p-1 mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 touch-none"
          title="Drag to reorder"
        >
          <Icon icon="mdi:drag" className="w-5 h-5" />
        </button>

        {/* Checkbox */}
        <input
          type="checkbox"
          checked={isAdded}
          disabled={isTableUI}
          onChange={(e) => {
            e.stopPropagation();
            onToggleWidget(e.target.checked);
          }}
          className={classNames(
            "h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded flex-shrink-0 mr-3",
            isTableUI ? "cursor-not-allowed opacity-60" : "cursor-pointer"
          )}
        />

        {/* Title - clickable for accordion */}
        <button
          onClick={onToggleExpand}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
        >
          <div className="min-w-0">
            <span className="text-sm font-medium text-gray-900 dark:text-white">{template.title}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{template.subtitle}</span>
          </div>
        </button>

        <div className="flex items-center gap-2 flex-shrink-0">
          {isTableUI && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium">
              Default
            </span>
          )}
          {isPending && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-medium">
              Pending
            </span>
          )}
          {/* Accordion chevron */}
          <button onClick={onToggleExpand} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <ChevronDownIcon
              className={classNames(
                "w-4 h-4 transition-transform duration-200",
                isExpanded ? "rotate-180" : ""
              )}
            />
          </button>
        </div>
      </div>

      {/* Widget content - accordion body */}
      {isExpanded && (
        <div className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
          {children}
        </div>
      )}
    </div>
  );
}

function WidgetsTab() {
  const {
    setEditMode,
    loadDashboard,
    availableWidgets,
    activeWidgets,
    addWidget,
    removeWidget,
    content,
    updateContent,
    resetDashboard,
    reorderWidgetTemplates,
    saveDashboard,
  } = useDashboard();
  const { selectedDeviceId } = useDeviceStore();

  // Local pending state: tracks checkbox changes before "Apply"
  const [pendingAdded, setPendingAdded] = useState<Set<string>>(new Set());
  const [pendingRemoved, setPendingRemoved] = useState<Set<string>>(new Set());
  const [hasChanges, setHasChanges] = useState(false);

  // Accordion state - tracks which widgets are expanded
  const [expandedWidgets, setExpandedWidgets] = useState<Set<string>>(new Set());
  const [mapConfigOpen, setMapConfigOpen] = useState(false);

  const toggleExpanded = (id: string) => {
    setExpandedWidgets((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Drag-and-drop sensors
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 5 },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: 200, tolerance: 5 },
  });
  const sensors = useSensors(pointerSensor, touchSensor);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      reorderWidgetTemplates(active.id as string, over.id as string, selectedDeviceId);
      setHasChanges(true);
    }
  };

  // Title/subtitle editing
  const [editTitle, setEditTitle] = useState(content.pageTitle);
  const [editSubtitle, setEditSubtitle] = useState(content.description);
  const [showResetModal, setShowResetModal] = useState(false);

  useEffect(() => {
    setEditTitle(content.pageTitle);
    setEditSubtitle(content.description);
  }, [content.pageTitle, content.description]);

  useEffect(() => {
    setEditMode(true);
    loadDashboard(selectedDeviceId || "default");
    return () => {
      setEditMode(false);
    };
  }, [setEditMode, loadDashboard, selectedDeviceId]);

  // Reset pending state when device changes
  useEffect(() => {
    setPendingAdded(new Set());
    setPendingRemoved(new Set());
    setHasChanges(false);
  }, [selectedDeviceId]);

  const isWidgetCurrentlyActive = (template: { type: string; title: string }) => {
    if (template.type === "table" && template.title === "Table UI") return true;
    return activeWidgets.some(
      (w) => w.type === template.type && w.title === template.title && w.isVisible !== false
    );
  };

  const isWidgetEffectivelyAdded = (template: { id: string; type: string; title: string }) => {
    if (template.type === "table" && template.title === "Table UI") return true;
    if (pendingAdded.has(template.id)) return true;
    if (pendingRemoved.has(template.id)) return false;
    return isWidgetCurrentlyActive(template);
  };

  const handleWidgetToggle = (template: WidgetTemplate, isChecked: boolean) => {
    if (template.type === "table" && template.title === "Table UI") return;

    const newAdded = new Set(pendingAdded);
    const newRemoved = new Set(pendingRemoved);

    if (isChecked) {
      if (isWidgetCurrentlyActive(template)) {
        newRemoved.delete(template.id);
      } else {
        newAdded.add(template.id);
        newRemoved.delete(template.id);
      }
    } else {
      if (isWidgetCurrentlyActive(template)) {
        newRemoved.add(template.id);
        newAdded.delete(template.id);
      } else {
        newAdded.delete(template.id);
      }
    }

    setPendingAdded(newAdded);
    setPendingRemoved(newRemoved);
    setHasChanges(newAdded.size > 0 || newRemoved.size > 0);
  };

  const handleApplyChanges = async () => {
    if (!selectedDeviceId) {
      toast.error("Select a device to save widget settings");
      return;
    }

    // Passing "" as deviceId suppresses each mutator's own auto-save;
    // the final state is saved once below to avoid racing overwrites.

    // Apply title/subtitle if changed
    if (editTitle !== content.pageTitle || editSubtitle !== content.description) {
      updateContent({ pageTitle: editTitle, description: editSubtitle }, "");
    }

    // Apply removals
    pendingRemoved.forEach((templateId) => {
      const template = availableWidgets.find((w) => w.id === templateId);
      if (template) {
        const widgetToRemove = activeWidgets.find(
          (w) => w.type === template.type && w.title === template.title && w.isVisible !== false
        );
        if (widgetToRemove) {
          removeWidget(widgetToRemove.id, "");
        }
      }
    });

    // Apply additions
    pendingAdded.forEach((templateId) => {
      const template = availableWidgets.find((w) => w.id === templateId);
      if (template) {
        addWidget(template, "");
      }
    });

    try {
      await saveDashboard(selectedDeviceId);
      setPendingAdded(new Set());
      setPendingRemoved(new Set());
      setHasChanges(false);
      toast.success("Widget changes applied successfully");
    } catch (error) {
      console.error("Failed to save widget settings:", error);
      toast.error("Failed to save widget settings. Please try again.");
    }
  };

  const handleReset = () => {
    setShowResetModal(true);
  };

  const handleResetConfirm = () => {
    if (!selectedDeviceId) {
      toast.error("Select a device to reset its dashboard");
      setShowResetModal(false);
      return;
    }
    resetDashboard(selectedDeviceId);
    setPendingAdded(new Set());
    setPendingRemoved(new Set());
    setHasChanges(false);
    setShowResetModal(false);
  };

  // Track title/subtitle changes for hasChanges
  const titleChanged = editTitle !== content.pageTitle || editSubtitle !== content.description;
  const anyChanges = hasChanges || titleChanged;

  // Render the actual widget UI for a given template type
  const renderWidgetContent = (template: WidgetTemplate) => {
    switch (template.type) {
      case "table":
        return (
          <div className="flex items-center justify-center h-32 text-gray-400 dark:text-gray-500 text-sm">
            Table widget is always enabled by default
          </div>
        );
      case "map":
        return (
          <div>
            {!mapConfigOpen ? (
              <div className="flex items-center justify-center h-32">
                <button
                  onClick={() => setMapConfigOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 shadow-sm transition-colors duration-200"
                >
                  <Icon icon="mdi:cog-outline" className="w-4 h-4" />
                  Configure Map
                </button>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Map Configuration</span>
                  <button
                    onClick={() => setMapConfigOpen(false)}
                    className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    Collapse
                  </button>
                </div>
                <FloorPlanUpload isEditMode={true} />
              </div>
            )}
          </div>
        );
      case "grid":
        return (
          <div className="flex items-center justify-center h-32 text-gray-400 dark:text-gray-500 text-sm">
            Grid widget preview
          </div>
        );
      case "graph":
        return (
          <div className="flex items-center justify-center h-32 text-gray-400 dark:text-gray-500 text-sm">
            Graph widget preview
          </div>
        );
      case "report":
        return <ReportWidget isEditMode={true} />;
      default:
        return (
          <div className="flex items-center justify-center h-32 text-gray-400 dark:text-gray-500 text-sm">
            Widget preview not available
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">Dashboard Widgets</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Add, remove, and configure widgets for your dashboard.
        </p>
      </div>

      {/* Location & Device Selectors */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Configure for</h4>
        <div className="flex items-center gap-3 flex-wrap">
          <LocationSelection />
          <DeviceSelection />
        </div>
      </div>

      {/* Dashboard Heading Settings */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Dashboard Heading</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Title</label>
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="e.g. IAQ"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Description</label>
            <input
              type="text"
              value={editSubtitle}
              onChange={(e) => setEditSubtitle(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="e.g. Indoor Air Quality Dashboard"
            />
          </div>
        </div>
      </div>

      {/* Widget Library — accordion UI with drag-and-drop reordering */}
      <div className="space-y-1">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Drag to reorder widgets. Click to expand and configure.
          </p>
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={[...availableWidgets]
              .filter((t) => t.type !== "report")
              .sort((a, b) => a.order - b.order)
              .map((t) => t.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {[...availableWidgets]
                .filter((t) => t.type !== "report")
                .sort((a, b) => a.order - b.order)
                .map((template) => {
                  const isAdded = isWidgetEffectivelyAdded(template);
                  const isTableUI = template.type === "table" && template.title === "Table UI";
                  const isPending = pendingAdded.has(template.id) || pendingRemoved.has(template.id);

                  return (
                    <SortableWidgetCard
                      key={template.id}
                      template={template}
                      isAdded={isAdded}
                      isTableUI={isTableUI}
                      isPending={isPending}
                      isExpanded={expandedWidgets.has(template.id)}
                      onToggleExpand={() => toggleExpanded(template.id)}
                      onToggleWidget={(checked) => handleWidgetToggle(template, checked)}
                    >
                      {renderWidgetContent(template)}
                    </SortableWidgetCard>
                  );
                })}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-2 pb-4 sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 -mx-6 px-6 mt-6">
        <button
          onClick={handleApplyChanges}
          disabled={!anyChanges || !selectedDeviceId}
          className={classNames(
            "inline-flex items-center px-5 py-2 text-sm font-medium rounded-md transition-colors",
            anyChanges && selectedDeviceId
              ? "bg-primary hover:bg-primary/90 text-white shadow-sm"
              : "bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
          )}
        >
          Apply Changes
        </button>
        <button
          onClick={handleReset}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
        >
          Reset Dashboard
        </button>
        {!selectedDeviceId && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            Select a device above to save widget settings.
          </p>
        )}
      </div>
      <DeleteConfirmationModal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        onConfirm={handleResetConfirm}
        title="Reset Dashboard"
        itemType="dashboard"
        customMessage="Are you sure you want to reset the dashboard? This will remove all widgets."
      />
    </div>
  );
}

// ── Billing Tab ──────────────────────────────────────────────────────────────

function BillingTab() {
  return (
    <div className="space-y-6 animate-pulse">
      {/* Header */}
      <div className="space-y-2">
        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-40" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-72" />
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20" />
            </div>
            <div className="h-7 bg-gray-200 dark:bg-gray-700 rounded w-12" />
          </div>
        ))}
      </div>

      {/* Monthly Cost Breakdown */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-t-lg flex items-center gap-2">
          <div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-44" />
        </div>
        <div className="p-4 space-y-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex justify-between items-center">
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-28" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-40" />
              </div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-12" />
            </div>
          ))}
          <hr className="border-gray-200 dark:border-gray-700" />
          <div className="flex justify-between items-center">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-28" />
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-16" />
          </div>
          <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700">
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16" />
          </div>
        </div>
      </div>

      {/* Location Details */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-t-lg">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32" />
        </div>
        <div className="p-4 space-y-3">
          {[0, 1].map((i) => (
            <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-3">
              <div className="flex justify-between items-center">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-44" />
                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded-full w-20" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[0, 1, 2].map((j) => (
                  <div key={j} className="bg-gray-50 dark:bg-gray-700 rounded-md p-2 space-y-1">
                    <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-3/4" />
                    <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-1/2" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-2">
        <div className="flex items-center gap-2 mb-2">
          <div className="h-4 w-4 bg-blue-200 dark:bg-blue-800 rounded" />
          <div className="h-4 bg-blue-200 dark:bg-blue-800 rounded w-36" />
        </div>
        <div className="h-3 bg-blue-200 dark:bg-blue-800 rounded w-full" />
        <div className="h-3 bg-blue-200 dark:bg-blue-800 rounded w-5/6" />
        <div className="h-3 bg-blue-200 dark:bg-blue-800 rounded w-4/6" />
        <div className="h-3 bg-blue-200 dark:bg-blue-800 rounded w-3/4" />
      </div>
    </div>
  );

}

// ── Main Settings Component ──────────────────────────────────────────────────

export default function Settings() {
  const isAdmin = useIsAdmin();
  // Widgets edits the org-wide dashboard layout; Access manages members and
  // Billing shows org billing — all admin-only, USERs are view-only
  const visibleTabs = TABS.filter(
    (t) => isAdmin || !["Widgets", "Access", "Billing"].includes(t.key),
  );
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab") as Tab | null;
  const [activeTab, setActiveTab] = useState<Tab>(
    tabFromUrl && visibleTabs.some((t) => t.key === tabFromUrl) ? tabFromUrl : "Profile",
  );

  useEffect(() => {
    const tab = searchParams.get("tab") as Tab | null;
    if (tab && visibleTabs.some((t) => t.key === tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Manage your account, team, widgets and billing.
        </p>
      </div>

      {/* Settings Card */}
      <div className="bg-white dark:bg-[#111B21] rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 min-h-[600px]">
        {/* Gradient Banner */}
        {/* <div className="h-14 rounded-t-xl bg-gradient-to-r from-gray-50 via-gray-100 to-gray-50 dark:from-gray-800/60 dark:via-gray-800/40 dark:to-gray-800/60 border-b border-gray-200 dark:border-gray-700" /> */}

        <div className="flex min-h-[540px]">
          {/* Left Tab Navigation */}
          <nav className="w-52 shrink-0 py-6 pl-6 pr-2 border-r border-gray-200 dark:border-gray-700">
            <div className="space-y-1">
              {visibleTabs.map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={classNames(
                      "flex items-center gap-3 w-full text-left px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 relative group",
                      isActive
                        ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                        : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200",
                    )}
                  >
                    <Icon
                      icon={tab.icon}
                      className={classNames(
                        "w-5 h-5 transition-colors duration-200",
                        isActive
                          ? "text-blue-600 dark:text-blue-400"
                          : "text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300",
                      )}
                    />
                    {tab.label}
                    {isActive && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-blue-500 rounded-r-full" />
                    )}
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Right Content */}
          <div className="flex-1 p-8 overflow-auto">
            {activeTab === "Profile" && <ProfileTab />}
            {activeTab === "Access" && isAdmin && <AccessTab />}
            {activeTab === "Widgets" && isAdmin && <WidgetsTab />}
            {activeTab === "Billing" && isAdmin && <BillingTab />}
          </div>
        </div>
      </div>
    </div>
  );
}
